<template>
    <div>
        <!-- Coquille stepper (sélection article / participants) : MASQUÉE en mode `slotOnly`, où
             seul le sélecteur de créneau ci-dessous est affiché (modale « dates requises »). -->
        <CustomDialog
            v-if="!slotOnly"
            :value="visible"
            @input="$emit('update:visible', $event)"
            width="85%"
            heightClass="ppv2-max-h-[90vh]"
            :hide-header="true"
            :no-scroll="true"
            customClassActions="!ppv2-justify-between md:ppv2-items-center"
        >
            <template #content>
                <div class="stepper-modal-v3">
                    <!-- Top Header — un SEUL titre, constant, aligné à gauche.
                     Ni onglets ni recherche : les trois types sont devenus le premier niveau du
                     menu vertical de gauche, et la recherche est passée en tête de ce même menu
                     (StepItemSelection). Tout ce qui sert à TROUVER un article tient désormais
                     dans une seule colonne.
                     Le titre ne reprend plus le libellé de l'étape : le fil du wizard, juste
                     en dessous, le dit déjà — le répéter en gros au-dessus n'apprenait rien et
                     faisait changer le titre du popup à chaque étape d'une même action. -->
                    <div class="modal-top-nav">
                        <div class="ppv2-text-black ppv2-text-lg poppinsBold">
                            {{ isEditing ? $t('devis.v3.edit_slot') : $t('devis.v3.add_to_quote') }}
                        </div>
                        <button class="close-btn-top" @click="close">
                            <X :size="24" />
                        </button>
                    </div>

                    <!-- Wizard Stepper — masqué sur les onglets « panier » (Resto, Options) : leur
                     ligne tient en une seule étape, afficher le parcours en trois étapes
                     laisserait croire qu'une progression est possible. -->
                    <div
                        v-if="!isBasketTab && wizardSteps.length > 1"
                        class="wizard-stepper-v3 ppv2-pt-4 ppv2-pb-5 ppv2-px-0 ppv2-w-[80%] ppv2-mx-auto ppv2-relative"
                    >
                        <div class="wizard-line-bg"></div>
                        <div class="wizard-line-progress" :style="{ width: progressWidth }"></div>
                        <div class="wizard-steps-row">
                            <div
                                v-for="s in wizardSteps"
                                :key="s"
                                class="wizard-step"
                                :class="{ active: step === s, completed: step > s }"
                                @click="step > s ? goToStep(s) : null"
                            >
                                <div class="dot-wrapper ppv2-bg-white dark:ppv2-bg-dark-popup">
                                    <Check v-if="step > s" :size="14" />
                                    <div v-else class="dot"></div>
                                </div>
                                <span class="step-label ppv2-text-black ppv2-text-opacity-50 poppinsMedium">{{
                                    getStepLabel(s)
                                }}</span>
                            </div>
                            <div class="wizard-step" :class="{ completed: step === 4 }">
                                <div
                                    class="dot-wrapper ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-text-black ppv2-text-opacity-70"
                                >
                                    <Check :size="14" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Main Content Area -->
                    <div class="stepper-body-v3 custom-scrollbar">
                        <!-- RÉCAPITULATIF — il occupe TOUTE la zone principale, à la place de
                             la liste des articles, et non un popover ancré au footer. La
                             sélection n'est pas perdue : elle est intacte derrière, on y
                             revient en refermant. -->
                        <selection-summary
                            v-show="summaryOpen"
                            :lines="summaryLines"
                            :total-ht="summaryTotalHt"
                            :total-ttc="summaryTotal"
                            :vat-groups="summaryVatGroups"
                            @close="summaryOpen = false"
                            @remove="handleSummaryRemove"
                            @edit="handleSummaryEdit"
                        />

                        <!-- `v-show` et NON `v-if` : les paniers resto et produits sont l'état
                             LOCAL de StepItemSelection. Le démonter le temps d'un aperçu viderait
                             la sélection, qu'on ne retrouverait pas en refermant. -->
                        <div v-show="!summaryOpen">
                            <transition name="fade-slide">
                                <!-- STEP 1: Item Selection
                             Accessible en modification (on peut revenir pour voir le contexte),
                             mais neutralisée : la ligne conserve son item_id, seuls le créneau,
                             les participants et la formule sont modifiables. -->
                                <div v-if="step === 1" :class="{ 'step-locked': isEditing }">
                                    <step-item-selection
                                        ref="itemSelection"
                                        :active-type="activeType"
                                        :type-tabs="typeTabs"
                                        :selected-id="selectedItem ? selectedItem.id : null"
                                        :search-query.sync="searchQuery"
                                        :item-details="itemDetails"
                                        :is-loading-details="isLoadingDetails"
                                        :live-price="livePrice"
                                        :item-note.sync="itemNote"
                                        :item-label.sync="itemLabel"
                                        :item-price.sync="itemPrice"
                                        :schedule-label="planningSummary"
                                        :staged-ids="stagedIds"
                                        :composed-by-type="composedByType"
                                        :composed-by-node="composedByNode"
                                        :uses-time-slots="usesTimeSlots"
                                        :pax.sync="pax"
                                        :pax-mixing.sync="paxMixing"
                                        :duration-id.sync="selectedDurationId"
                                        :ticket-selections.sync="ticketSelections"
                                        :lock-space.sync="paxLockSpace"
                                        :participants-initialised.sync="participantsInitialised"
                                        @type-change="selectTypeTab"
                                        @open-planning="openPlanning"
                                        @remove-experience="removeExperience"
                                        @select="handleItemSelect"
                                        @resto-change="handleRestoChange"
                                        @product-change="handleProductChange"
                                    />
                                </div>

                                <!-- STEP 2: Participants -->
                                <step-participants
                                    v-if="step === 2"
                                    v-model="pax"
                                    :item-details="itemDetails"
                                    :mixing.sync="paxMixing"
                                    :duration-id.sync="selectedDurationId"
                                    :ticket-selections.sync="ticketSelections"
                                    :uses-time-slots="usesTimeSlots"
                                    :lock-space.sync="paxLockSpace"
                                    :initialised.sync="participantsInitialised"
                                />
                            </transition>
                        </div>
                    </div>
                </div>
            </template>
            <!-- Remove default Save button from CustomDialog -->
            <template #actions>
                <!-- Groupe de GAUCHE : le retour, puis le récapitulatif de la sélection. -->
                <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                    <BaseButton
                        v-if="step > 1"
                        customClass="!ppv2-rounded-xl !ppv2-px-8 !poppinsBold ppv2-bg-white dark:ppv2-bg-dark-popup"
                        variant="outline"
                        @click="step--"
                    >
                        {{ $t('devis.v3.back') }}
                    </BaseButton>
                    <!-- RÉCAPITULATIF de la sélection — un seul bloc pour les trois types, là où
                 trois bandeaux mutuellement exclusifs n'en montraient qu'un à la fois, celui
                 de l'onglet courant. Fermé il annonce le total et le nombre de lignes ; ouvert
                 il déplie le détail au-dessus du footer.
                 Il n'écrit rien : la validation reste le bouton de droite. -->
                    <!-- Bascule compacte : elle n'ouvre et ne referme que l'aperçu. La
                         flèche pointe vers le haut tant qu'il est replié, vers le bas une fois
                         déplié. -->
                    <button
                        v-if="summaryLines.length > 0"
                        type="button"
                        class="summary-toggle"
                        :aria-expanded="summaryOpen ? 'true' : 'false'"
                        @click="summaryOpen = !summaryOpen"
                    >
                        <span class="summary-toggle-text">
                            <span class="summary-toggle-lines">{{
                                $tc('devis.v3.summary_lines', summaryLines.length, { count: summaryLines.length })
                            }}</span>
                            <span class="summary-toggle-total poppinsSemiBold">{{ formatEuro(summaryTotal) }}</span>
                        </span>
                        <ChevronDown v-if="summaryOpen" :size="18" class="summary-toggle-caret" />
                        <ChevronUp v-else :size="18" class="summary-toggle-caret" />
                    </button>
                </div>

                <BaseButton
                    variant="primary"
                    @click="handleNext"
                    :disabled="isNextDisabled"
                    customClass="!ppv2-rounded-xl !ppv2-px-8 !poppinsBold"
                >
                    <template v-if="isLoadingDetails && step === 1">
                        {{ $t('common.loading') || 'Chargement...' }}
                    </template>
                    <template v-else-if="isEditing">
                        {{ $t('devis.v3.confirm') }}
                    </template>
                    <template v-else>
                        {{ $t('devis.v3.confirm') }}<span v-if="composedCount > 0"> ({{ composedCount }})</span>
                    </template>
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- CHOIX DU CRÉNEAU — popup POSÉE SUR la popup principale, comme la configuration d'un
             plat le faisait avant elle. Isolée et minimale : un titre, le calendrier, les
             créneaux, et rien d'autre. La refermer laisse la popup principale intacte derrière,
             avec l'article et ses réglages toujours en place — on ne recompose rien.

             `is-in-popup` retire le décalage de la sidebar ; `width` reprend la main sur la
             largeur réduite que ce mode impose par défaut. -->
        <CustomDialog
            :value="showPlanning"
            @input="$event || closePlanning()"
            is-in-popup
            width="46%"
            heightClass="ppv2-max-h-[90vh]"
            :hide-header="true"
            customClassActions="!ppv2-justify-between ppv2-items-center"
        >
            <template #content>
                <div class="planning-popup-v3">
                    <!-- Header violet, fixe en tête : ce qu'on fait, puis sur quoi. -->
                    <div class="planning-head-v3">
                        <div class="planning-head-text">
                            <span class="planning-head-eyebrow">{{ $t('devis.v3.edit_slot') }}</span>
                            <span class="planning-head-title poppinsBold">{{ planningItemName }}</span>
                        </div>
                        <button type="button" class="planning-head-close" @click="closePlanning">
                            <X :size="20" />
                        </button>
                    </div>

                    <div class="planning-body-v3">
                        <!-- Trois façons de dater une ligne, proposées UNIQUEMENT quand le devis
                             ne pose pas de réservation : dans un devis avec créneau, la ligne
                             bloque des places et ne peut pas rester sans date. -->
                        <div v-if="showPlanningModes" class="planning-modes-v3">
                            <button
                                v-for="mode in planningModes"
                                :key="mode.key"
                                type="button"
                                class="planning-mode-btn poppinsMedium"
                                :class="{ active: planningMode === mode.key }"
                                @click="planningMode = mode.key"
                            >
                                {{ $t(mode.labelKey) }}
                            </button>
                        </div>

                        <!-- MODE « Choisir une date » : date et heure libres. -->
                        <planning-free-date v-if="planningMode === 'date'" v-model="freeDate" />

                        <!-- MODE « Sans date » : rien à choisir, on l'annonce. -->
                        <div v-else-if="planningMode === 'none'" class="planning-empty-v3">
                            <div class="planning-empty-icon"><Info :size="22" /></div>
                            <p class="planning-empty-title poppinsBold">{{ $t('devis.v3.planning_none_title') }}</p>
                            <p class="planning-empty-text">{{ $t('devis.v3.planning_none_text') }}</p>
                        </div>

                        <!-- MODE « Bloquer un créneau » : la liste de créneaux existante, telle
                             quelle. `itemDetails` conditionne le montage : sans lui, StepPlanning
                             lancerait un premier appel /slots incomplet — sans la formule ni les
                             billets retenus, d'où un prix vide sur un simpleTicket. -->
                        <step-planning
                            v-else-if="itemDetails"
                            :selected-item="selectedItem"
                            :item-details="itemDetails"
                            :pax="pax"
                            :force-mixing="paxMixing"
                            :duration-id="selectedDurationId"
                            :ticket-selections="ticketSelections"
                            :uses-time-slots="usesTimeSlots"
                            :initial-date="editingDate"
                            v-model="selectedSlot"
                        />
                    </div>
                </div>
            </template>

            <template #actions>
                <!-- Le pied résume CE QUI SERA ÉCRIT sur la ligne : la date retenue, ou le fait
                     qu'il n'y en aura pas. -->
                <span class="planning-foot-summary">{{ planningSummary }}</span>

                <div class="planning-foot-actions">
                    <BaseButton
                        customClass="!ppv2-rounded-xl !ppv2-px-8 !poppinsBold ppv2-bg-white dark:ppv2-bg-dark-popup"
                        variant="outline"
                        @click="closePlanning"
                    >
                        {{ $t('devis.v3.back') }}
                    </BaseButton>

                    <BaseButton
                        variant="primary"
                        :disabled="isPlanningConfirmDisabled"
                        customClass="!ppv2-rounded-xl !ppv2-px-8 !poppinsBold"
                        @click="confirmPlanning"
                    >
                        {{ $t('devis.v3.confirm') }}
                    </BaseButton>
                </div>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import StepItemSelection from './components/StepItemSelection.vue'
import StepParticipants from './components/StepParticipants.vue'
import StepPlanning from './components/StepPlanning.vue'
import PlanningFreeDate from './components/PlanningFreeDate.vue'
import SelectionSummary from './components/SelectionSummary.vue'
import moment from 'moment'
import { X, Check, Info, ChevronUp, ChevronDown } from 'lucide-vue'

export default {
    name: 'QuoteItemStepper',
    components: {
        CustomDialog,
        BaseButton,
        StepItemSelection,
        StepParticipants,
        StepPlanning,
        PlanningFreeDate,
        SelectionSummary,
        Info,
        ChevronUp,
        ChevronDown,
        X,
        Check,
    },
    props: {
        visible: { type: Boolean, required: true },
        // Mode de composition du devis (résolu côté serveur). En mode SANS créneau, aucune
        // réservation n'est posée : le nombre de personnes se saisit librement et un créneau
        // complet reste choisissable. Défaut `true` = mode le plus contraint.
        usesTimeSlots: { type: Boolean, default: true },
        // Ligne de devis en cours de modification. Absente = flux d'AJOUT, strictement inchangé.
        // Présente, la popup s'ouvre directement sur l'étape Planning avec le créneau courant
        // présélectionné, et la navigation entre étapes est libre.
        editingItem: { type: Object, default: null },
        // Mode « sélecteur de créneau isolé » (opt-in, défaut `false` → éditeur inchangé).
        // Combiné à `editingItem`, seule la popup planning est rendue — la coquille stepper
        // (étapes article/participants) est masquée — et la fermer/valider referme tout le
        // sélecteur (émission `update:visible=false`). Utilisé par la modale « dates requises ».
        slotOnly: { type: Boolean, default: false },
    },
    data() {
        return {
            step: 1,
            activeType: 'experience',
            searchQuery: '',
            // Prix de la ligne pour la formule et le nombre de participants courants, calculé
            // SERVEUR par la fonction qui tarife les créneaux. `null` tant qu'on ne l'a pas :
            // l'écran retombe alors sur le prix catalogue plutôt que d'afficher un montant faux.
            livePrice: null,
            livePriceToken: 0,
            // Le choix du créneau est une popup POSÉE SUR la principale, plus une étape du corps.
            // La refermer ne défait rien : on retrouve l'article et ses réglages derrière.
            showPlanning: false,
            // Façon de dater la ligne : 'slot' (créneau existant), 'date' (date et heure
            // libres) ou 'none' (aucune date). Seul le mode SANS créneau propose les trois —
            // un devis qui réserve ne peut pas laisser une ligne sans date.
            planningMode: this.defaultPlanningMode(),
            // Prestations composées et mises de côté, en attente de la validation du lot.
            // Chaque entrée est la charge utile d'un /add-item : le lot n'invente rien, il
            // enchaîne ce que l'ajout unitaire aurait envoyé.
            pendingExperiences: [],
            // Récapitulatif replié par défaut : le footer reste compact tant qu'on ne le
            // demande pas, comme le veut l'écran.
            summaryOpen: false,
            isSubmittingBatch: false,
            // { date: 'YYYY-MM-DD', time: 'HH:mm' } retenus en mode date libre.
            freeDate: null,
            // Note libre et libellé personnalisé de la ligne, saisis DÈS l'ajout. Ils vivent ici
            // parce qu'ils partent avec la réservation (handleFinish), au même titre que la
            // formule ou le nombre de participants.
            itemNote: '',
            itemLabel: '',
            // Prix TTC négocié sur la ligne. `null` = on garde celui que le serveur calcule pour
            // la formule et le nombre de participants courants.
            itemPrice: null,
            typeTabs: [
                { id: 'experience', label: this.$t('devis.v3.experiences') },
                // { id: 'package', label: this.$t('devis.v3.packages') },
                { id: 'option', label: this.$t('devis.v3.options') },
                { id: 'resto', label: this.$t('devis.v3.resto') },
            ],
            selectedItem: null,
            itemDetails: null,
            pax: 1,
            paxMixing: false,
            // Portés ici car l'étape Participants est détruite/recréée par son v-if : en data
            // locale, la privatisation et les valeurs par défaut se réappliquaient à chaque
            // retour arrière et écrasaient la saisie.
            paxLockSpace: false,
            participantsInitialised: false,
            selectedDurationId: null,
            ticketSelections: {},
            selectedSlot: null,
            isLoadingDetails: false,
            // Panier RESTO remonté par l'étape 1. `payload` est déjà dans la forme
            // attendue par l'API ; le reste ne sert qu'au récapitulatif du pied de popup.
            restoBasket: { entries: [], count: 0, total: 0, payload: { foods: [], packs: [] } },
            isSubmittingResto: false,
            // Panier PRODUITS (onglet Options), même principe que le resto : la charge utile
            // est déjà prête pour l'API, le reste alimente le récapitulatif du pied de popup.
            productBasket: { entries: [], count: 0, total: 0, payload: { products: [] } },
            isSubmittingProducts: false,
        }
    },
    watch: {
        // Retirer la DERNIÈRE ligne depuis le récapitulatif ferait disparaître la bascule du
        // footer tout en laissant le panneau ouvert sur un tableau vide : on revient à la liste
        // des articles, seul endroit d'où l'on peut recomposer.
        'summaryLines.length'(count) {
            if (count === 0) this.summaryOpen = false
        },
        // Toute variation de l'article, de la formule ou du nombre de participants redemande le
        // prix. Les trois sont surveillés ensemble : ils entrent dans le même calcul.
        priceInputs: {
            immediate: true,
            handler() {
                this.fetchLivePrice()
            },
        },
        visible(val) {
            if (!val) {
                this.reset()
                return
            }
            if (this.editingItem) {
                this.prefillFromEditingItem()
            }
        },
    },
    computed: {
        /**
         * Étapes affichées dans le fil.
         *
         * À l'AJOUT, les participants sont réglés directement sous l'article sélectionné : le
         * parcours ne compte plus que « Article » puis « Date ». En MODIFICATION, l'étape 1 est
         * verrouillée (on ne change pas la prestation d'une ligne) : l'écran Participants reste
         * donc le seul endroit où corriger le nombre de personnes, et le fil le montre.
         */
        /**
         * Étapes du corps de la popup principale.
         *
         * Le créneau n'en fait plus partie : il a sa propre popup. À l'AJOUT il ne reste donc
         * qu'un écran — le fil n'a plus rien à montrer et se masque. En MODIFICATION, l'étape 1
         * est verrouillée (on ne change pas la prestation d'une ligne) et l'écran Participants
         * reste le seul endroit où corriger le nombre de personnes.
         */
        planningModes() {
            return [
                { key: 'date', labelKey: 'devis.v3.planning_mode_date' },
                { key: 'slot', labelKey: 'devis.v3.planning_mode_slot' },
                { key: 'none', labelKey: 'devis.v3.planning_mode_none' },
            ]
        },
        /**
         * Les trois modes ne se proposent qu'en devis SANS créneau.
         *
         * Dans un devis qui réserve, la ligne consomme des places : la laisser sans date, ou sur
         * une date libre sans créneau, produirait exactement le devis mixte que le figeage de
         * `make_devis_with_time_slot` sert à empêcher.
         */
        showPlanningModes() {
            return !this.usesTimeSlots
        },
        /**
         * Une décision de date a-t-elle été prise ? « Sans date » en est une : ce qui bloque,
         * c'est de n'avoir rien choisi du tout.
         */
        /**
         * La prestation en cours est-elle prête à partir ? Mêmes conditions que l'ancien bouton
         * de validation : un article, ses détails, un nombre de participants et une date
         * tranchée.
         */
        currentExperienceReady() {
            // Volontairement INDÉPENDANT de l'onglet affiché : une prestation composée reste
            // composée quand on va voir le resto. La lier à `isBasketTab` faisait chuter le
            // compteur au simple changement d'onglet.
            if (!this.selectedItem || !this.itemDetails || this.isLoadingDetails) return false
            if (!this.pax || this.pax <= 0) return false

            return this.planningDecided
        },
        // Identifiants des articles déjà composés : la liste les affiche comme retenus.
        stagedIds() {
            return this.pendingExperiences.map((entry) => Number(entry.id))
        },
        /**
         * Ce qui est composé, PAR TYPE. Le menu s'en sert pour signaler où l'on a déjà fait des
         * choix : sans ce repère, passer au resto donne l'impression d'avoir tout perdu.
         *
         * Les clés sont celles de `typeTabs` — le menu n'a rien à déduire.
         */
        composedByType() {
            return {
                experience: this.pendingExperiences.length + (this.currentExperienceReady ? 1 : 0),
                option: this.productBasket.count,
                resto: this.restoBasket.count,
            }
        },
        /**
         * Lignes composées par NŒUD du menu, clé « param:value » — la même que celle du rail.
         *
         * Seules les expériences y figurent : elles se rangent par tag, alors que les plats et
         * les produits sont des paniers, comptés au niveau du type.
         *
         * Le report vers les tags parents se fait à l'affichage : ici on ne compte QUE le tag
         * porté par l'article, sans quoi une ligne serait comptée deux fois.
         */
        composedByNode() {
            const map = {}
            const add = (key, quantity) => {
                if (!key) return
                map[key] = (map[key] || 0) + quantity
            }

            // EXPÉRIENCES : rangées par tag, une ligne à la fois.
            const addExperience = (item) => add(item?.tag?.id ? 'tag:' + item.tag.id : null, 1)

            this.pendingExperiences.forEach((entry) => addExperience(entry.draft?.item))
            if (this.currentExperienceReady)
                addExperience(this.selectedItem)

                // PRODUITS : rangés par sous-catégorie boutique, à défaut par catégorie. Le badge
                // compte des UNITÉS, comme celui du type — deux comptages différents sur le même
                // écran se contrediraient.
            ;(this.productBasket.entries || []).forEach((entry) => {
                const key = entry.subcategory_id
                    ? 'subcategory:' + entry.subcategory_id
                    : entry.category_id
                      ? 'category:' + entry.category_id
                      : null

                add(key, entry.quantity || 0)
            })

            // RESTO : rangé par slug de catégorie, la clé du rail.
            ;(this.restoBasket.entries || []).forEach((entry) => {
                add(entry.category_slug ? 'resto:' + entry.category_slug : null, entry.quantity || 0)
            })

            return map
        },
        // Nombre de lignes qui partiront à la validation, tous types confondus.
        composedCount() {
            return (
                this.pendingExperiences.length +
                (this.currentExperienceReady ? 1 : 0) +
                this.restoBasket.count +
                this.productBasket.count
            )
        },
        /**
         * RÉCAPITULATIF — la composition en cours, mise à plat en lignes affichables.
         *
         * Les trois sources sont exactement celles que `handleAddBatch` enverra : les
         * prestations mises de côté, celle en cours si elle est complète, puis les deux
         * paniers. Une ligne ici = une ligne écrite en base, dans le même ordre.
         *
         * Les montants sont présentés en HT, comme sur le devis lui-même. Ni le taux ni le HT
         * ne sont inventés : le taux vient du catalogue (`vat_rate`), et le HT du serveur
         * (`/items/{id}/price`, qui appelle `VspService::calculatePriceHt`, le helper même de
         * l'écriture). `lineHt` ne sert que de repli.
         */
        summaryLines() {
            const lines = []

            this.pendingExperiences.forEach((entry, index) => {
                lines.push(this.experienceSummaryLine(entry, 'staged-' + index))
            })

            if (this.currentExperienceReady) {
                lines.push(
                    this.experienceSummaryLine({ payload: this.buildFinalItem(), draft: this.snapshotDraft() }, 'draft', true)
                )
            }

            ;(this.restoBasket.entries || []).forEach((entry, index) => {
                const configured = (entry.lines || []).reduce((sum, line) => sum + (line.options?.length || 0), 0)
                const vatRate = Number(entry.vat_rate) || 0
                const totalTtc = Number(entry.total) || 0

                lines.push({
                    key: 'resto-' + (entry.uuid || index),
                    type: 'resto',
                    sourceId: entry.uuid,
                    isDraft: false,
                    name: entry.name,
                    image: entry.image || null,
                    qty: entry.quantity,
                    vatRate,
                    vatBreakdown: this.singleVatBreakdown(totalTtc, vatRate),
                    totalHt: this.lineHt(totalTtc, vatRate),
                    totalTtc,
                    meta: configured > 0 ? [this.$tc('devis.v3.food_choices_count', configured, { count: configured })] : [],
                })
            })
            ;(this.productBasket.entries || []).forEach((entry, index) => {
                const vatRate = Number(entry.vat_rate) || 0
                const totalTtc = Number(entry.total) || 0

                lines.push({
                    key: 'option-' + (entry.id || index),
                    type: 'option',
                    sourceId: entry.id,
                    isDraft: false,
                    name: entry.name,
                    image: entry.image || null,
                    qty: entry.quantity,
                    vatRate,
                    vatBreakdown: this.singleVatBreakdown(totalTtc, vatRate),
                    totalHt: this.lineHt(totalTtc, vatRate),
                    totalTtc,
                    meta: [],
                })
            })

            return lines
        },
        summaryTotalHt() {
            return this.summaryLines.reduce((sum, line) => sum + (Number(line.totalHt) || 0), 0)
        },
        summaryTotal() {
            return this.summaryLines.reduce((sum, line) => sum + (Number(line.totalTtc) || 0), 0)
        },
        /**
         * VENTILATION DE LA TVA par taux — une entrée par taux rencontré, avec sa base HT et
         * son montant, comme sur le devis. Un taux à 0 % n'est pas affiché : il n'apporte
         * aucune ligne de TVA (billets d'un pass, articles exonérés).
         */
        summaryVatGroups() {
            const byRate = new Map()

            this.summaryLines.forEach((line) => {
                ;(line.vatBreakdown || []).forEach((part) => {
                    const rate = Number(part.rate) || 0
                    if (rate <= 0) return

                    const current = byRate.get(rate) || { rate, baseHt: 0, amount: 0 }
                    current.baseHt += Number(part.baseHt) || 0
                    current.amount += Number(part.amount) || 0
                    byRate.set(rate, current)
                })
            })

            return [...byRate.values()].sort((a, b) => a.rate - b.rate)
        },
        planningDecided() {
            // « Sans date » est le défaut d'un devis sans créneau, et un défaut se passe de
            // confirmation : la ligne part telle quelle, ou l'utilisateur ouvre la popup pour
            // la dater. C'est ce qui permet d'enchaîner les prestations sans un aller-retour
            // par la popup à chaque ligne.
            if (this.planningMode === 'none') return true
            if (this.planningMode === 'date') return !!(this.freeDate?.date && this.freeDate?.time)

            return !!this.selectedSlot
        },
        planningItemName() {
            return this.selectedItem?.name || ''
        },
        isPlanningConfirmDisabled() {
            if (this.planningMode === 'none') return false
            if (this.planningMode === 'date') return !this.freeDate?.date || !this.freeDate?.time

            return !this.selectedSlot
        },
        /**
         * Ce que le pied annonce : la date qui sera écrite, ou son absence assumée.
         */
        planningSummary() {
            if (this.planningMode === 'none') return this.$t('devis.v3.planning_none_summary')

            if (this.planningMode === 'date') {
                if (!this.freeDate?.date) return ''

                return moment(this.freeDate.date).format('ddd D MMMM') + ' · ' + this.freeDate.time
            }

            return this.selectedSlot?.time || ''
        },
        wizardSteps() {
            return this.isEditing ? [1, 2] : [1]
        },
        priceInputs() {
            return [this.selectedItem?.id || null, this.selectedDurationId, this.pax]
        },
        progressWidth() {
            // Calculate progress line percentage based on steps
            // 4 dots = 3 gaps. Gap 1 (step 2), Gap 2 (step 3), Gap 3 (step 4)
            if (this.step === 4) return '100%'

            // Progression déduite du RANG de l'étape dans le fil, et non de son numéro : le
            // parcours d'ajout n'en compte que deux, celui de modification trois.
            const index = this.wizardSteps.indexOf(this.step)
            if (index <= 0) return '0%'

            return ((index / this.wizardSteps.length) * 100).toFixed(2) + '%'
        },
        isNextDisabled() {
            // Un seul bouton pour les trois onglets : désactivé tant que RIEN n'a été composé,
            // actif dès qu'une ligne — de n'importe quel type — est prête.
            if (this.step === 1 && !this.isEditing) {
                return this.isSubmittingBatch || this.composedCount === 0
            }

            if (this.step === 1 && this.isRestoTab) {
                return this.isSubmittingResto || this.restoBasket.count === 0
            }
            // Produits : même parcours en une étape — un produit n'a ni participants ni créneau.
            if (this.step === 1 && this.isProductTab) {
                return this.isSubmittingProducts || this.productBasket.count === 0
            }
            // Le contrôle de l'ancienne étape 2 (`pax > 0`) est repris ici : il porte désormais
            // sur l'étape 1, qui héberge le réglage des participants. Le créneau, lui, est
            // contrôlé par le bouton de sa propre popup.
            if (this.step === 1) {
                if (!this.selectedItem || this.isLoadingDetails || !this.itemDetails) return true
                if (this.isEditing) return false
                if (!this.pax || this.pax <= 0) return true

                // On n'enregistre pas une ligne dont la date n'a pas été tranchée — même
                // « sans date » est une décision, prise dans la popup.
                return !this.planningDecided
            }
            if (this.step === 2) return !this.pax || this.pax <= 0
            return false
        },
        isEditing() {
            return !!this.editingItem
        },
        // L'onglet Resto tient en une seule étape : ni participants, ni créneau.
        isRestoTab() {
            return this.activeType === 'resto'
        },
        // Idem pour l'onglet Options (produits boutique) : la ligne est plate, comme en
        // caisse nomade — on la valide directement depuis l'étape 1.
        isProductTab() {
            return this.activeType === 'option'
        },
        // Les deux onglets « panier » : une seule étape, un compteur, une validation directe.
        isBasketTab() {
            return this.isRestoTab || this.isProductTab
        },
        // Date de la ligne rouverte : le planning doit s'ouvrir dessus, sinon le créneau
        // présélectionné n'est pas dans la semaine affichée et reste invisible.
        editingDate() {
            return this.editingItem?.start_time ? String(this.editingItem.start_time).substring(0, 10) : null
        },
        canGoToStep() {
            return (s) => {
                if (s === 1) return true
                if (s === 2) return !!this.selectedItem
                if (s === 3) return !!this.selectedItem && this.pax && this.pax > 0
                return false
            }
        },
    },
    methods: {
        /**
         * Reconstruit l'état du stepper à partir d'une ligne existante, pour rouvrir la MÊME
         * popup que la création, positionnée sur l'étape Planning avec son créneau sélectionné.
         *
         * La présélection ne demande aucune adaptation de StepPlanning : il compare déjà
         * `value.start_time` + `value.spaceId` (isSlotSelected). On lui fournit donc un objet
         * de la même forme que celui qu'il émet.
         */
        async prefillFromEditingItem() {
            const item = this.editingItem

            this.selectedItem = {
                id: item.item_id,
                slug: item.slug || null,
                price: item.price,
            }
            this.pax = parseInt(item.nb_contributor || item.quantity) || 1
            this.paxMixing = !!item.authorize_mixage
            this.paxLockSpace = !item.authorize_mixage
            this.participantsInitialised = true
            this.selectedDurationId = item.duration_id || null
            this.ticketSelections = item.ticket_selections || {}

            // Créneau courant, dans la forme émise par StepPlanning.
            const start = String(item.start_time || '')
            const end = String(item.end_time || '')
            this.selectedSlot = {
                date: start.substring(0, 10),
                start: start.substring(11, 16),
                start_time: start.substring(11, 16),
                end: end.substring(11, 16),
                end_time: end.substring(11, 16),
                spaceId: item.space_id || null,
            }

            // La modification porte sur le CRÉNEAU : on ouvre directement sa popup, la
            // principale restant en fond sur l'étape 1 verrouillée.
            this.step = 1
            this.showPlanning = true

            // Les détails alimentent la durée, le slug et le type utilisés pour charger les
            // créneaux : sans eux, StepPlanning interrogerait l'API avec une formule vide.
            this.isLoadingDetails = true
            try {
                const { data } = await axios.get(`/back-office/v3/devis/items/${item.item_id}/details?type=experience`)
                this.itemDetails = data
                this.selectedItem = { ...this.selectedItem, slug: data.slug || this.selectedItem.slug }
                if (!this.selectedDurationId) {
                    this.selectedDurationId = data.duration_id || null
                }
            } catch (error) {
                console.error('Impossible de charger les détails de la prestation à modifier :', error)
            } finally {
                this.isLoadingDetails = false
            }
        },
        reset() {
            this.step = 1
            this.activeType = 'experience'
            this.searchQuery = ''
            this.selectedItem = null
            this.pax = null
            this.paxMixing = false
            this.paxLockSpace = false
            this.participantsInitialised = false
            this.selectedDurationId = null
            this.ticketSelections = {}
            this.selectedSlot = null
            this.isLoadingDetails = false
            this.restoBasket = { entries: [], count: 0, total: 0, payload: { foods: [], packs: [] } }
            this.isSubmittingResto = false
            this.productBasket = { entries: [], count: 0, total: 0, payload: { products: [] } }
            this.isSubmittingProducts = false
            this.itemNote = ''
            this.itemLabel = ''
            this.itemPrice = null
            this.livePrice = null
            this.showPlanning = false
            this.planningMode = this.defaultPlanningMode()
            this.freeDate = null
            this.pendingExperiences = []
            this.summaryOpen = false
            this.isSubmittingBatch = false
        },
        close() {
            this.showPlanning = false
            this.$emit('update:visible', false)
        },
        selectTypeTab(typeId) {
            if (typeId === this.activeType) return

            // Quitter l'onglet ne doit pas perdre la prestation en cours : si elle est complète,
            // on la met de côté, comme lorsqu'on passe à un autre article. Sans cela, composer
            // deux expériences puis aller au resto en effaçait une.
            if (this.currentExperienceReady) this.stageExperience()

            this.activeType = typeId
            this.resetExperienceDraft()
        },
        async handleItemSelect(item) {
            // La prestation d'une ligne en cours de modification est figée.
            if (this.isEditing) return

            // Onglets « panier » (Resto, Options) : la sélection alimente un panier local à
            // l'étape 1. Aucun appel de détails, aucun passage à l'étape suivante.
            if (this.isBasketTab) return

            // Re-cliquer l'article EN COURS ne doit RIEN faire.
            //
            // Cette garde passe AVANT la mise de côté automatique : sans cela, recliquer la
            // ligne en cours la rangerait puis la rouvrirait comme neuve, en écrasant ses
            // réglages — formule, participants, note, prix — qui vivent maintenant sur la ligne.
            if (this.selectedItem && Number(this.selectedItem.id) === Number(item.id)) return

            // Passer à un AUTRE article ne doit pas perdre celui en cours : s'il est complet, on
            // le met de côté tout seul. C'est ce qui permet d'enchaîner les sélections sans
            // penser à valider chaque ligne — sans lui, choisir B décochait A.
            //
            // AVANT la réouverture ci-dessous : rouvrir une ligne rangée alors qu'une autre est
            // en cours de composition perdrait cette dernière.
            if (this.currentExperienceReady) this.stageExperience()

            // Article DÉJÀ mis de côté : on le ROUVRE en composition au lieu d'en préparer un
            // second exemplaire. Recliquer une ligne composée doit la reprendre, pas la doubler.
            if (this.unstageExperience(item.id)) return

            this.selectedItem = item
            // Nouvelle prestation : ses propres valeurs par défaut doivent s'appliquer, et la
            // note comme le libellé saisis pour la PRÉCÉDENTE ne doivent pas la suivre.
            this.participantsInitialised = false
            this.paxLockSpace = false
            this.itemNote = ''
            this.itemLabel = ''
            this.itemPrice = null
            this.isLoadingDetails = true
            try {
                const response = await axios.get(`/back-office/v3/devis/items/${item.id}/details?type=${this.activeType}`)
                this.itemDetails = response.data
                // Set default pax based on min_contributors, explicitly parsing to integer
                if (this.itemDetails.subcategory_slug === 'simpleTicket') {
                    this.pax = 0
                } else if (this.itemDetails.min_contributors) {
                    this.pax = parseInt(this.itemDetails.min_contributors)
                } else {
                    this.pax = 1
                }

                // Fallback to the first possible ticket if min_contributors is invalid/overridden
                if (this.itemDetails.possible_tickets?.length > 0 && !this.itemDetails.possible_tickets.includes(this.pax)) {
                    this.pax = this.itemDetails.possible_tickets[0]
                }
                this.paxMixing = false
                this.selectedDurationId = this.itemDetails.duration_id || null
            } catch (error) {
                console.error('Error fetching item details:', error)
                this.itemDetails = null
                this.pax = 1
            } finally {
                this.isLoadingDetails = false
            }
        },
        /**
         * Va chercher le prix de la ligne pour la formule et le nombre de participants courants.
         *
         * Aucun calcul ici : le serveur rejoue la fonction qui tarife les créneaux, de sorte que
         * le montant annoncé sur la ligne soit celui que l'étape Date affichera.
         *
         * Un jeton d'appel écarte les réponses en retard : trois clics rapides sur « + » lancent
         * trois requêtes, et rien ne garantit qu'elles reviennent dans l'ordre.
         */
        async fetchLivePrice() {
            const item = this.selectedItem

            if (!item || this.isRestoTab || this.isProductTab || !this.pax || this.pax <= 0) {
                this.livePrice = null
                return
            }

            const token = ++this.livePriceToken

            try {
                const { data } = await axios.get(`/back-office/v3/devis/items/${item.id}/price`, {
                    params: {
                        durationId: this.selectedDurationId || undefined,
                        numberOfParticipation: this.pax,
                    },
                })

                if (token === this.livePriceToken) this.livePrice = data
            } catch (error) {
                // Un prix indisponible ne doit pas bloquer la composition : la ligne retombe sur
                // le prix catalogue, et l'étape Date donnera le montant définitif.
                if (token === this.livePriceToken) this.livePrice = null
            }
        },
        /**
         * Mode de datation posé par défaut sur une prestation qui vient d'être choisie.
         *
         * Sans créneau : « sans date ». La ligne est alors immédiatement complète — on peut
         * l'ajouter, ou passer à la suivante, sans ouvrir la popup ; celle-ci ne sert plus
         * qu'à ceux qui VEULENT dater.
         *
         * Avec créneau : « créneau », le seul mode qu'un tel devis accepte. Rien n'y change,
         * la popup y reste obligatoire — une ligne qui bloque des places doit être datée,
         * c'est ce que le figeage de `make_devis_with_time_slot` protège.
         */
        defaultPlanningMode() {
            return this.usesTimeSlots ? 'slot' : 'none'
        },
        openPlanning() {
            // Un devis AVEC créneau n'a qu'une façon de dater une ligne : le créneau.
            if (!this.showPlanningModes) this.planningMode = 'slot'

            this.showPlanning = true
        },
        /**
         * Referme la popup de créneau SANS rien défaire : l'article, la formule, les
         * participants, la note et le prix restent tels quels dans la popup principale.
         *
         * En modification, on revient sur l'écran Participants : l'étape 1 y étant verrouillée,
         * c'est le seul endroit où corriger le nombre de personnes.
         */
        /**
         * Validation de la popup de date.
         *
         * À l'AJOUT elle ne fait que retenir le choix et refermer : la ligne s'enregistre depuis
         * la popup principale, une fois tout composé. En MODIFICATION, la popup EST le geste —
         * elle applique directement.
         */
        confirmPlanning() {
            if (this.isEditing) {
                this.handleFinish()
                return
            }

            this.showPlanning = false
        },
        closePlanning() {
            this.showPlanning = false
            // Mode isolé : il n'y a pas de coquille stepper derrière où revenir — fermer le
            // planning referme tout le sélecteur et rend la main à la modale « dates requises ».
            if (this.slotOnly) {
                this.close()
                return
            }
            if (this.isEditing) this.step = 2
        },
        getStepLabel(s) {
            const labels = {
                1: this.$t('devis.v3.step_selection'),
                2: this.$t('devis.v3.step_participants'),
                3: this.$i18n.locale === 'fr' ? 'Date' : 'Date',
            }
            return labels[s]
        },
        /**
         * Panier resto remonté par l'étape 1.
         *
         * On le CONSERVE tel quel, charge utile comprise : la traduction en payload
         * d'API vit dans `foodQuotePayload`, appelée là où le panier est composé.
         */
        handleRestoChange(basket) {
            this.restoBasket = basket || { entries: [], count: 0, total: 0, payload: { foods: [], packs: [] } }
        },
        /** Panier produits remonté par l'étape 1 — même contrat que le resto. */
        handleProductChange(basket) {
            this.productBasket = basket || { entries: [], count: 0, total: 0, payload: { products: [] } }
        },
        handleNext() {
            // En modification, l'étape 1 est verrouillée : on passe par l'écran Participants,
            // seul endroit pour corriger le nombre de personnes.
            if (this.step === 1 && this.isEditing) {
                this.step = 2
                return
            }

            if (this.step === 1) {
                // Un seul geste enregistre TOUT ce qui a été composé — prestations, plats et
                // produits — quel que soit l'onglet affiché.
                this.submitBatch()
                return
            }

            this.handleFinish()
        },
        /**
         * Validation du panier resto : on remonte la charge utile à la page du devis, qui
         * seule connaît le client, le devis en cours et l'échéance.
         *
         * La popup ne se ferme QU'APRÈS un enregistrement réussi : en cas d'erreur serveur
         * le panier est encore là, l'utilisateur n'a pas à tout recomposer.
         */
        async handleFinishResto() {
            const payload = this.restoBasket.payload || { foods: [], packs: [] }

            if (payload.foods.length === 0 && payload.packs.length === 0) return

            this.isSubmittingResto = true
            try {
                await new Promise((resolve, reject) => {
                    this.$emit('add-foods', { payload, resolve, reject })
                })
                this.close()
            } catch (error) {
                // Le message d'erreur est affiché par la page du devis (toast) : ici on se
                // contente de laisser la popup ouverte avec le panier intact.
            } finally {
                this.isSubmittingResto = false
            }
        },
        /**
         * Validation du panier produits. Strictement le même contrat que le resto : la page
         * du devis reçoit la charge utile, enregistre, et résout/rejette la promesse. La
         * popup ne se ferme qu'après un enregistrement réussi.
         */
        async handleFinishProducts() {
            const payload = this.productBasket.payload || { products: [] }

            if (payload.products.length === 0) return

            this.isSubmittingProducts = true
            try {
                await new Promise((resolve, reject) => {
                    this.$emit('add-products', { payload, resolve, reject })
                })
                this.close()
            } catch (error) {
                // Message affiché par la page du devis ; panier laissé intact.
            } finally {
                this.isSubmittingProducts = false
            }
        },
        formatEuro(value) {
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value) || 0)
        },
        /**
         * Horaires à écrire sur la ligne, selon la façon de la dater.
         *
         * - créneau : les bornes du créneau retenu, comportement d'origine ;
         * - date libre : la date et l'heure choisies, la fin déduite de la durée de la formule ;
         * - sans date : `null`, la ligne se datera plus tard depuis le devis.
         */
        buildSchedule() {
            if (this.planningMode === 'none') {
                return { startTime: null, endTime: null }
            }

            if (this.planningMode === 'date') {
                const start = moment(`${this.freeDate.date} ${this.freeDate.time}`, 'YYYY-MM-DD HH:mm')
                const minutes = parseInt(this.itemDetails?.duration_minutes, 10)

                return {
                    startTime: start.format('YYYY-MM-DD HH:mm'),
                    // Une durée absente ne doit pas produire une fin invalide : à défaut, la fin
                    // vaut le début, comme pour une ligne qu'on ne sait pas borner.
                    endTime: start
                        .clone()
                        .add(Number.isFinite(minutes) ? minutes : 0, 'minutes')
                        .format('YYYY-MM-DD HH:mm'),
                }
            }

            const slot = this.selectedSlot
            const date = slot.date // already YYYY-MM-DD from StepPlanning

            // On privilégie les bornes DATÉES fournies par le serveur (start_at / end_at). Les
            // recomposer à partir de la seule date du jour donnait une fin antérieure au début pour
            // un créneau franchissant minuit (22:00 → 01:00), rejeté ensuite par la validation.
            return {
                startTime: slot.start_at || `${date} ${(slot.start || slot.start_time || '').substring(0, 5)}`,
                endTime: slot.end_at || `${date} ${(slot.end || slot.end_time || '').substring(0, 5)}`,
            }
        },
        /**
         * La charge utile d'UNE ligne d'expérience, telle que /add-item l'attend.
         *
         * Extraite de handleFinish pour être réutilisable : composer plusieurs lignes revient à
         * en construire plusieurs et à les enregistrer l'une après l'autre.
         */
        buildFinalItem() {
            const slot = this.selectedSlot
            const { startTime, endTime } = this.buildSchedule()

            // Aucune borne haute ici : une prestation peut être planifiée après l'échéance du
            // devis, qui n'est qu'un délai d'acceptation (cf. QuoteScheduleGuard).
            // Sans créneau retenu (date libre, sans date), le prix de référence est celui que
            // le serveur vient de calculer pour la formule et les participants.
            const slotPrice = parseFloat(
                this.selectedSlot?.price ||
                    this.selectedSlot?.price_slot ||
                    this.livePrice?.unit_price ||
                    this.selectedItem.price ||
                    0
            )

            // Note et libellé partent AVEC la réservation : la ligne naît complète, sans
            // aller-retour « j'ajoute, je rouvre, je complète ». `null` quand rien n'a été saisi,
            // pour que le serveur laisse la ligne telle qu'il l'aurait créée.
            const trimmedNote = (this.itemNote || '').trim()
            const trimmedLabel = (this.itemLabel || '').trim()

            const finalItem = {
                // Display fields (for the table in Create.vue)
                type: 'prestation',
                id: this.selectedItem.id,
                name: this.selectedItem.name,
                qty: this.pax,
                unit_price: slotPrice,
                slot: slot,

                // Backend payload fields
                prestationSlug: this.itemDetails?.slug || this.selectedItem.slug || '',
                space: {
                    id: slot?.spaceId || slot?.details_price?.[0]?.space_id || this.itemDetails?.space_id || 0,
                    numberOfParticipation: this.pax,
                    level: this.itemDetails?.level || null,
                },
                reservationType: {
                    duration: this.itemDetails?.duration_minutes || null,
                    durationId: this.selectedDurationId || this.itemDetails?.duration_id || null,
                    startTime,
                    endTime,
                },
                options: {
                    rental: [],
                    purchase: [],
                },
                authorize_mixage: this.paxMixing,
                comment: trimmedNote !== '' ? trimmedNote : null,
                // Prix TTC négocié. `null` = le serveur garde le tarif qu'il vient de calculer.
                // Seul le TTC part : SyncTvaOrder recalcule le HT depuis lui et le taux catalogue.
                price: Number.isFinite(this.itemPrice) && this.itemPrice >= 0 ? this.itemPrice : null,
                custom_label: trimmedLabel !== '' && trimmedLabel !== this.selectedItem.name ? trimmedLabel : null,
                // Prix du slot transmis explicitement pour simpleTicket
                slot_price: slotPrice,
                // Breakdown of tickets for simpleTicket (melimetric logic)
                listActivities: {
                    purchase: Object.entries(this.ticketSelections)
                        .filter(([_, qty]) => qty > 0)
                        .map(([id, qty]) => ({
                            id: parseInt(id),
                            type: 'prestation',
                            quantity: qty,
                        })),
                },
            }
            return finalItem
        },
        handleFinish() {
            const finalItem = this.buildFinalItem()

            if (this.isEditing) {
                this.$emit('update', { order_item_id: this.editingItem.id, item: finalItem })
            } else {
                this.$emit('add', finalItem)
            }
            this.showPlanning = false
            this.close()
        },
        /**
         * Met la prestation composée DE CÔTÉ et rend l'écran disponible pour la suivante.
         *
         * Rien n'est envoyé : le lot part d'un seul coup à la validation. C'est ce qui permet
         * de composer plusieurs lignes — et de plusieurs types — sans fermer la popup.
         */
        /**
         * Retire une prestation de la composition — qu'elle soit déjà mise de côté ou en cours.
         *
         * C'est le pendant du × des lignes resto et produits : le seul moyen de revenir sur une
         * sélection sans avoir à valider le devis pour ensuite supprimer la ligne.
         */
        /**
         * × d'une ligne du récapitulatif — la ligne est DÉSÉLECTIONNÉE, exactement comme si
         * on l'avait retirée depuis la liste des articles.
         *
         * Les paniers resto et produits sont l'état local de StepItemSelection : on lui
         * demande le retrait plutôt que de dupliquer ici une copie de son panier.
         */
        handleSummaryRemove(line) {
            if (line.type === 'experience') {
                if (line.isDraft) {
                    this.resetExperienceDraft()
                    return
                }

                this.removeExperience({ id: line.sourceId })
                return
            }

            this.$refs.itemSelection?.removeSummaryEntry(line.type, line.sourceId)
        },
        /**
         * Crayon d'une ligne — retour LÀ OÙ elle a été composée : son onglet, et pour une
         * prestation déjà mise de côté, sa reprise en composition (même geste qu'un clic sur
         * sa ligne dans la liste).
         */
        handleSummaryEdit(line) {
            this.summaryOpen = false

            if (this.activeType !== line.type) this.selectTypeTab(line.type)

            // APRÈS le changement d'onglet : `selectTypeTab` met de côté la composition en
            // cours et repart d'un brouillon vierge, ce qui écraserait la reprise.
            if (line.type === 'experience' && !line.isDraft) this.unstageExperience(line.sourceId)
        },
        removeExperience(item) {
            const staged = this.pendingExperiences.find((entry) => Number(entry.id) === Number(item.id))

            if (staged) {
                this.pendingExperiences = this.pendingExperiences.filter((entry) => entry !== staged)
                return
            }

            if (this.selectedItem && Number(this.selectedItem.id) === Number(item.id)) {
                this.resetExperienceDraft()
            }
        },
        stageExperience() {
            if (!this.currentExperienceReady) return

            this.pendingExperiences = [
                ...this.pendingExperiences,
                {
                    id: this.selectedItem.id,
                    name: this.selectedItem.name,
                    payload: this.buildFinalItem(),
                    // Le brouillon complet, pour pouvoir rouvrir la ligne exactement telle
                    // qu'elle a été composée. La charge utile seule ne suffirait pas : elle est
                    // aplatie pour l'API et ne dit rien du mode de datation retenu.
                    draft: this.snapshotDraft(),
                },
            ]
            this.resetExperienceDraft()
        },
        /**
         * Photo de la composition en cours — tout ce qui distingue cette ligne des autres.
         */
        /**
         * HT d'une ligne à partir de son TTC et de son taux — REPLI seulement.
         *
         * Même opération que `VspService::calculatePriceHt`, qui fait foi à l'écriture. Elle
         * ne sert que là où le serveur ne fournit pas le HT : les paniers resto et produits,
         * dont le taux vient du catalogue, et un prix négocié saisi à la main.
         */
        /** Ventilation d'une ligne à taux unique : une seule entrée. */
        singleVatBreakdown(totalTtc, vatRate) {
            const ht = this.lineHt(totalTtc, vatRate)

            return [{ rate: Number(vatRate) || 0, baseHt: ht, amount: (Number(totalTtc) || 0) - ht }]
        },
        lineHt(totalTtc, vatRate) {
            const rate = Number(vatRate) || 0
            const ttc = Number(totalTtc) || 0

            return rate > 0 ? ttc / (1 + rate / 100) : ttc
        },
        /**
         * Date et heure d'une ligne, en français, SANS dépendre de la locale de moment.
         *
         * `moment.locale()` est un réglage GLOBAL que plusieurs écrans posent au montage : s'y
         * fier ici ferait dépendre le libellé de l'ordre de chargement des composants, et le
         * jour s'afficherait en anglais sur une page qui n'a chargé aucun d'eux. moment ne sert
         * donc qu'à lire la date ; c'est `Intl` qui la met en forme, comme pour les montants.
         */
        formatScheduleChip(startTime) {
            const start = moment(startTime)

            const day = new Intl.DateTimeFormat('fr-FR', {
                weekday: 'short',
                day: '2-digit',
                month: '2-digit',
            }).format(start.toDate())

            return day + ' · ' + start.format('HH:mm')
        },
        /**
         * Une prestation — mise de côté ou en cours — traduite en ligne de récapitulatif.
         *
         * Elle lit la MÊME charge utile que celle qui partira à l'API, plus le brouillon
         * conservé à la mise de côté : ce qui s'affiche ici est donc ce qui sera écrit, sans
         * chemin de calcul parallèle.
         */
        experienceSummaryLine(entry, key, isDraft = false) {
            const payload = entry.payload || {}
            const draft = entry.draft || {}
            const details = draft.itemDetails || {}
            const name = payload.custom_label || payload.name || ''
            const image = draft.item?.image || draft.item?.img1 || null
            const meta = []

            const startTime = payload.reservationType?.startTime
            meta.push(startTime ? this.formatScheduleChip(startTime) : this.$t('devis.v3.summary_no_date'))

            const minutes = parseInt(details.duration_minutes, 10)
            if (Number.isFinite(minutes) && minutes > 0) {
                meta.push(this.$t('devis.v3.summary_duration_minutes', { count: minutes }))
            }

            // BILLET GROUPÉ — la ligne principale est enregistrée à 0 €, chaque billet porte
            // son propre tarif ET son propre taux. Le montant de la ligne est donc la somme
            // des billets retenus, et le taux affiché n'a de sens que s'ils partagent le même.
            if (details.subcategory_slug === 'simpleTicket') {
                const selections = draft.ticketSelections || {}
                const rates = new Set()
                const vatBreakdown = []
                let qty = 0
                let totalTtc = 0
                let totalHt = 0

                ;(details.list_activites || []).forEach((activity) => {
                    const picked = parseInt(selections[activity.id], 10) || 0
                    if (picked <= 0) return

                    const price = Number(activity.price) || 0
                    const rate = Number(activity.vat_rate) || 0
                    const ttc = picked * price
                    const ht = this.lineHt(ttc, rate)

                    qty += picked
                    totalTtc += ttc
                    totalHt += ht
                    rates.add(rate)
                    // Ventilation billet par billet : deux billets de taux différents sur le
                    // même pass doivent alimenter DEUX lignes de TVA. Ventiler au niveau de la
                    // ligne les ferait disparaître, et le total cesserait de s'équilibrer.
                    vatBreakdown.push({ rate, baseHt: ht, amount: ttc - ht })
                    meta.push(picked + ' × ' + activity.name)
                })

                if (payload.comment) meta.push(payload.comment)

                return {
                    key,
                    type: 'experience',
                    // De quoi remonter à la source : l'article d'origine, et si la ligne est
                    // celle EN COURS de composition ou une ligne déjà mise de côté.
                    sourceId: payload.id ?? entry.id ?? null,
                    isDraft,
                    name,
                    image,
                    qty,
                    // Taux unique s'il n'y en a qu'un, sinon rien : afficher « 20 % » sur un
                    // pass mêlant deux taux serait faux. La ventilation, elle, les garde tous.
                    vatRate: rates.size === 1 ? [...rates][0] : null,
                    vatBreakdown,
                    totalHt,
                    totalTtc,
                    meta,
                }
            }

            // Prix négocié saisi à l'ajout : c'est lui qui sera écrit, il prime donc sur le
            // tarif calculé par le serveur — et son HT doit être re-dérivé, le serveur ayant
            // renvoyé celui du tarif catalogue.
            const negotiated = Number.isFinite(payload.price) ? Number(payload.price) : null
            const live = draft.livePrice || null
            const qty = parseInt(live?.quantity, 10) || parseInt(payload.qty, 10) || 1
            const vatRate = Number(live?.vat_rate) || 0
            const unitPrice = negotiated !== null ? negotiated : Number(live?.unit_price ?? payload.unit_price) || 0
            const totalTtc = unitPrice * qty

            // HT du serveur quand le tarif n'a pas été renégocié : c'est le montant exact qui
            // sera écrit. Sinon on retombe sur la même formule que lui.
            const totalHt =
                negotiated === null && Number.isFinite(Number(live?.unit_price_ht))
                    ? Number(live.unit_price_ht) * qty
                    : this.lineHt(totalTtc, vatRate)

            if (negotiated !== null) meta.push(this.$t('devis.v3.summary_negotiated_price'))
            if (payload.comment) meta.push(payload.comment)

            return {
                key,
                type: 'experience',
                sourceId: payload.id ?? entry.id ?? null,
                isDraft,
                name,
                image,
                qty,
                vatRate,
                vatBreakdown: [{ rate: vatRate, baseHt: totalHt, amount: totalTtc - totalHt }],
                totalHt,
                totalTtc,
                meta,
            }
        },
        snapshotDraft() {
            return {
                item: this.selectedItem,
                itemDetails: this.itemDetails,
                pax: this.pax,
                paxMixing: this.paxMixing,
                paxLockSpace: this.paxLockSpace,
                selectedDurationId: this.selectedDurationId,
                ticketSelections: { ...this.ticketSelections },
                selectedSlot: this.selectedSlot,
                itemNote: this.itemNote,
                itemLabel: this.itemLabel,
                itemPrice: this.itemPrice,
                livePrice: this.livePrice,
                freeDate: this.freeDate,
                planningMode: this.planningMode,
            }
        },
        restoreDraft(draft) {
            this.selectedItem = draft.item
            this.itemDetails = draft.itemDetails
            this.pax = draft.pax
            this.paxMixing = draft.paxMixing
            this.paxLockSpace = draft.paxLockSpace
            this.participantsInitialised = true
            this.selectedDurationId = draft.selectedDurationId
            this.ticketSelections = { ...draft.ticketSelections }
            this.selectedSlot = draft.selectedSlot
            this.itemNote = draft.itemNote
            this.itemLabel = draft.itemLabel
            this.itemPrice = draft.itemPrice
            this.livePrice = draft.livePrice
            this.freeDate = draft.freeDate
            this.planningMode = draft.planningMode
        },
        /**
         * Retire une ligne de la réserve et la ramène en composition.
         *
         * C'est ce qui évite les doublons : recliquer un article déjà composé le ROUVRE au lieu
         * d'en préparer un second exemplaire.
         */
        unstageExperience(itemId) {
            const entry = this.pendingExperiences.find((e) => Number(e.id) === Number(itemId))
            if (!entry) return false

            this.pendingExperiences = this.pendingExperiences.filter((e) => e !== entry)
            this.restoreDraft(entry.draft)

            return true
        },
        /**
         * Remet à zéro la seule composition d'expérience en cours, sans toucher aux paniers
         * resto et produits ni aux lignes déjà mises de côté.
         */
        resetExperienceDraft() {
            this.selectedItem = null
            this.itemDetails = null
            this.pax = null
            this.paxMixing = false
            this.paxLockSpace = false
            this.participantsInitialised = false
            this.selectedDurationId = null
            this.ticketSelections = {}
            this.selectedSlot = null
            this.itemNote = ''
            this.itemLabel = ''
            this.itemPrice = null
            this.livePrice = null
            this.freeDate = null
            this.planningMode = this.defaultPlanningMode()
        },
        /**
         * Envoie TOUT ce qui a été composé : les prestations mises de côté, celle en cours si
         * elle est complète, puis les paniers resto et produits.
         *
         * La page du devis enchaîne les enregistrements dans cet ordre — ajouter trois lignes
         * d'un coup revient exactement à les ajouter une par une.
         *
         * La popup ne se referme QU'APRÈS un enregistrement réussi : sur erreur, tout ce qui a
         * été composé est encore là.
         */
        async submitBatch() {
            const experiences = this.pendingExperiences.map((entry) => entry.payload)
            if (this.currentExperienceReady) experiences.push(this.buildFinalItem())

            const foods = this.restoBasket.payload || { foods: [], packs: [] }
            const products = this.productBasket.payload || { products: [] }

            if (experiences.length === 0 && this.restoBasket.count === 0 && this.productBasket.count === 0) return

            this.isSubmittingBatch = true
            try {
                await new Promise((resolve, reject) => {
                    this.$emit('add-batch', { experiences, foods, products, resolve, reject })
                })
                this.close()
            } catch (error) {
                // Le message est affiché par la page du devis : ici on laisse la popup ouverte
                // avec la composition intacte.
            } finally {
                this.isSubmittingBatch = false
            }
        },
        goToStep(targetStep) {
            if (this.canGoToStep(targetStep)) {
                this.step = targetStep
            }
        },
    },
}
</script>

<style scoped>
/* Étape de sélection neutralisée en modification : aucun clic ne passe (onglets, recherche,
   cartes), et le grisage indique que la prestation n'est pas modifiable ici. */
.step-locked {
    pointer-events: none;
    opacity: 0.55;
    filter: grayscale(0.3);
}

.stepper-modal-v3 {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    border-radius: 24px;
    overflow: hidden;
}

.modal-top-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    z-index: 10;
}

.close-btn-top {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a1f37;
    cursor: pointer;
    transition: all 0.2s;
}

.close-btn-top:hover {
    background: #e2e8f0;
    transform: scale(1.05);
}

.wizard-line-bg {
    height: 2px;
    background: #e2e8f0;
}

.wizard-line-progress {
    height: 2px;
    background: #6347f3;
    transition: width 0.4s ease;
    /* max-width: calc(100% - 218px);*/
}

.wizard-steps-row {
    display: flex;
    justify-content: space-between;
    position: relative;
    top: -16px;
    z-index: 2;
}

.wizard-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 0;
}

.dot-wrapper {
    width: 26px;
    height: 26px;
    border: 2px solid #e2e8f0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
}

.wizard-step.active .dot-wrapper {
    border-color: #6347f3;
}

.wizard-step.active .dot {
    width: 10px;
    height: 10px;
    background: #6347f3;
    border-radius: 50%;
}

.wizard-step.completed .dot-wrapper {
    background: #6347f3;
    border-color: #6347f3;
    color: white;
}

.wizard-step:last-child .dot-wrapper {
    background: #cbd5e1;
    border-color: #cbd5e1;
}

.wizard-step:last-child.completed .dot-wrapper {
    background: #cbd5e1;
}

.step-label {
    font-size: clamp(11px, 0.625227vw, 14px);
    text-align: center;
    white-space: nowrap;
    position: absolute;
    top: 35px;
}

.wizard-step.completed .step-label,
.wizard-step.active .step-label {
    color: #5932ea;
}

.stepper-body-v3 {
    flex: 1;
    overflow-y: auto;
    padding: 0;
    position: relative;
    overflow-x: hidden;
}

/* Bascule du récapitulatif : deux lignes empilées — le nombre de lignes, puis le total —
   et la flèche qui dit dans quel sens le clic agit. */
.summary-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px;
    border: 1px solid #e9edf3;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}

.summary-toggle:hover {
    border-color: #7d2dbd;
    box-shadow: 0 2px 10px rgba(125, 45, 189, 0.12);
}

.summary-toggle-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.25;
}

.summary-toggle-lines {
    font-size: 11px;
    color: #1d1d1b;
    opacity: 0.55;
}

.summary-toggle-total {
    font-size: 14px;
    color: #1d1d1b;
    font-variant-numeric: tabular-nums;
}

.summary-toggle-caret {
    color: #1d1d1b;
    opacity: 0.5;
}

.modal-footer-v3 {
    padding: 20px 40px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #f1f5f9;
}

.btn-back-v3 {
    font-weight: 600;
    color: #64748b;
    background: none;
    cursor: pointer;
    padding: 10px 20px;
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.3s ease;
}

.fade-slide-leave-active {
    position: absolute;
    width: 100%;
}

.fade-slide-enter {
    opacity: 0;
    transform: translateX(20px);
}

.fade-slide-leave-to {
    opacity: 0;
    transform: translateX(-20px);
}

@media (min-width: 768.99px) and (max-width: 1470px) {
    span.step-label.ppv2-text-black.ppv2-text-opacity-50.poppinsMedium {
        width: 130px;
        white-space: normal;
    }
}

@media (max-width: 768px) {
    .stepper-modal-v3 .wizard-stepper-v3 {
        margin-bottom: 25px;
    }

    span.step-label.ppv2-text-black.ppv2-text-opacity-50.poppinsMedium {
        width: 90px;
        white-space: normal;
    }
}

@media (max-width: 1200px) {
    .wizard-line-progress {
        gap: 8px;
    }
}

/* Popup de créneau : volontairement minimale — un titre, une croix, puis le calendrier et les
   créneaux. Aucun fil d'étapes : elle n'en a qu'une. */
.planning-popup-v3 {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.planning-popup-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0 14px;
    margin-bottom: 4px;
    border-bottom: 1px solid #e9edf3;
}

.close-btn-sm {
    width: 36px;
    height: 36px;
}

/* ------------------------------------------------------------------ *
 *  Popup de créneau : header violet, modes, corps, pied
 * ------------------------------------------------------------------ */

.planning-popup-v3 {
    display: flex;
    flex-direction: column;
    min-height: 0;
    margin: -16px -16px 0;
}

/* Header fixe en tête, aux couleurs de l'application. Le libellé dit l'action, le titre dit
   sur quoi elle porte. */
.planning-head-v3 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 62px;
    padding: 10px 18px;
    border-radius: 12px 12px 0 0;
    background: #7d2dbd;
    color: #fff;
    flex: 0 0 auto;
}

.planning-head-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.planning-head-eyebrow {
    font-family: 'Poppins', sans-serif;
    font-size: 10.5px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    opacity: 0.75;
}

.planning-head-title {
    font-size: 16px;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.planning-head-close {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    cursor: pointer;
    transition: background 0.18s ease;
}

.planning-head-close:hover {
    background: rgba(255, 255, 255, 0.26);
}

.planning-body-v3 {
    padding: 16px 18px 4px;
    min-height: 0;
    overflow-y: auto;
}

/* Sélecteur à trois modes : trois zones de largeur égale dans une piste grise, l'actif en
   blanc sur violet. */
.planning-modes-v3 {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 4px;
    padding: 4px;
    margin-bottom: 16px;
    border-radius: 9999px;
    background: #f1f5f9;
}

.planning-mode-btn {
    padding: 8px 6px;
    border: 0;
    border-radius: 9999px;
    background: transparent;
    color: #64748b;
    font-size: 13px;
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease,
        box-shadow 0.2s ease;
}

.planning-mode-btn:hover:not(.active) {
    color: #7d2dbd;
}

.planning-mode-btn.active {
    background: #fff;
    color: #7d2dbd;
    box-shadow: 0 1px 4px rgba(29, 29, 27, 0.12);
}

/* « Sans date » : un écran qui n'a rien à demander doit le dire clairement, et occuper la
   même hauteur que les autres pour que le modal ne saute pas d'un mode à l'autre. */
.planning-empty-v3 {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 260px;
    padding: 24px;
    text-align: center;
}

.planning-empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: rgba(125, 45, 189, 0.1);
    color: #7d2dbd;
}

.planning-empty-title {
    margin: 0;
    color: #1d1d1b;
    font-size: 15px;
}

.planning-empty-text {
    margin: 0;
    max-width: 42ch;
    color: #64748b;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    line-height: 1.5;
}

.planning-foot-summary {
    color: #475569;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
}

.planning-foot-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}
</style>
